(() => {
  const roots = new WeakMap();
  const ROOT_SELECTOR = '[data-rvr3-root], [data-rvp-root]';
  const STAGE_STARTS = [0, 0.25, 0.5, 0.75];
  const FILM_MAP = [
    { scroll: 0, film: 0 },
    { scroll: 0.25, film: 0.16 },
    { scroll: 0.5, film: 0.4 },
    { scroll: 0.75, film: 0.64 },
    { scroll: 1, film: 1 },
  ];
  const mobileMedia = matchMedia('(max-width: 720px)');
  const reducedMedia = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value) => Math.min(1, Math.max(0, value));
  const smooth = (value) => {
    const n = clamp(value);
    return n * n * (3 - 2 * n);
  };
  const mapFilm = (value) => {
    const endIndex = FILM_MAP.findIndex((point) => point.scroll >= value);
    if (endIndex <= 0) return FILM_MAP[0].film;
    const start = FILM_MAP[endIndex - 1];
    const end = FILM_MAP[endIndex];
    return start.film + ((value - start.scroll) / (end.scroll - start.scroll)) * (end.film - start.film);
  };
  const constrainedDevice = () => {
    const connection = navigator.connection;
    return Boolean(
      connection?.saveData ||
      /(^|-)2g$/.test(connection?.effectiveType || '') ||
      Number(navigator.deviceMemory || 8) <= 4,
    );
  };

  const initCanopy = (stage, clean) => {
    const sticky = stage.querySelector('.rvr3-canopy__sticky');
    const canvas = stage.querySelector('canvas');
    const poster = stage.querySelector('.rvr3-canopy__poster');
    const video = stage.querySelector('[data-rvr3-scrub]');
    const beats = [...stage.querySelectorAll('[data-rvr3-beat]')];
    const context = canvas?.getContext('2d', { alpha: false, desynchronized: true });
    if (!sticky || !context || !video) return;
    if (reducedMedia.matches || navigator.connection?.saveData) {
      stage.dataset.mediaState = 'fallback';
      return;
    }

    const frameCount = Number(stage.dataset.count || 107);
    const sourceReady = stage.dataset.sourceReady !== 'false' && Boolean(video.dataset.src);
    const aborter = new AbortController();
    let duration = 0;
    let targetFrame = 0;
    let targetTime = 0;
    let renderedFrame = -1;
    let sourceLoaded = false;
    let near = false;
    let raf = 0;
    let destroyed = false;
    let objectUrl = '';
    let loadAbort = null;
    let seekTimer = 0;
    let lastSeek = 0;

    const progress = () => {
      const rect = stage.getBoundingClientRect();
      const stickyTop = parseFloat(getComputedStyle(sticky).top) || 0;
      return clamp((stickyTop - rect.top) / Math.max(1, rect.height - sticky.offsetHeight));
    };

    const updateInterface = (value) => {
      const active = STAGE_STARTS.reduce((state, start, index) => (value >= start ? index : state), 0);
      stage.style.setProperty('--canopy-progress', value.toFixed(4));
      beats.forEach((beat, index) => {
        const selected = index === active;
        beat.classList.toggle('is-active', selected);
        beat.setAttribute('aria-hidden', String(!selected));
      });
    };

    const feather = (x, y, width, height) => {
      const backdrop = '#e7e8e3';
      const amount = Math.min(36, Math.max(20, height * 0.05));
      const right = x + width;
      const bottom = y + height;
      const fades = [
        [context.createLinearGradient(0, y, 0, y + amount), x, y, width, amount, [[0, backdrop], [1, 'rgba(231,232,227,0)']]],
        [context.createLinearGradient(0, bottom - amount, 0, bottom), x, bottom - amount, width, amount, [[0, 'rgba(231,232,227,0)'], [1, backdrop]]],
      ];
      if (x > 0) fades.push([context.createLinearGradient(x, 0, x + amount, 0), x, y, amount, height, [[0, backdrop], [1, 'rgba(231,232,227,0)']]]);
      if (right < canvas.width) fades.push([context.createLinearGradient(right - amount, 0, right, 0), right - amount, y, amount, height, [[0, 'rgba(231,232,227,0)'], [1, backdrop]]]);
      fades.forEach(([gradient, left, top, widthValue, heightValue, stops]) => {
        stops.forEach(([at, color]) => gradient.addColorStop(at, color));
        context.fillStyle = gradient;
        context.fillRect(left, top, widthValue, heightValue);
      });
    };

    const paint = () => {
      if (destroyed || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
      const actual = duration > 0
        ? Math.round(clamp(video.currentTime / duration) * (frameCount - 1))
        : targetFrame;
      if (actual === renderedFrame && stage.dataset.ready === 'true') return;
      const mobile = mobileMedia.matches;
      const portrait = matchMedia('(min-width: 601px) and (max-width: 1024px) and (orientation: portrait)').matches;
      const scale = Math.min(canvas.width / video.videoWidth, canvas.height / video.videoHeight) * (mobile ? 1.1 : 1);
      const width = video.videoWidth * scale;
      const height = video.videoHeight * scale;
      const frameProgress = actual / Math.max(1, frameCount - 1);
      const x = (canvas.width - width) / 2;
      const mobilePan = clamp((frameProgress - 0.46) / 0.54);
      const startY = mobile ? canvas.height * (0.57 - mobilePan * 0.24) - height / 2 : (canvas.height - height) / 2;
      const liftStart = mobile || portrait ? 0.46 : 0.16;
      const lift = clamp((frameProgress - liftStart) / Math.max(0.01, 1 - liftStart));
      const y = startY * (1 - smooth(lift));
      context.fillStyle = '#e7e8e3';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(video, x, y, width, height);
      feather(x, y, width, height);
      renderedFrame = actual;
      stage.dataset.mediaState = 'ready';
      stage.dataset.ready = 'true';
      poster?.setAttribute('aria-hidden', 'true');
    };

    const commitSeek = () => {
      seekTimer = 0;
      if (!duration || video.readyState < 1) return;
      const frameDuration = duration / Math.max(1, frameCount - 1);
      if (Math.abs(video.currentTime - targetTime) <= frameDuration * 0.45) {
        paint();
        return;
      }
      lastSeek = performance.now();
      video.currentTime = targetTime;
    };
    const seekLatest = () => {
      if (!duration || video.readyState < 1 || seekTimer) return;
      const wait = Math.max(0, 40 - (performance.now() - lastSeek));
      if (wait > 0) seekTimer = setTimeout(commitSeek, wait);
      else commitSeek();
    };

    const update = () => {
      const value = progress();
      targetFrame = Math.round(mapFilm(value) * (frameCount - 1));
      targetTime = duration > 0 ? (targetFrame / Math.max(1, frameCount - 1)) * Math.max(0, duration - 0.001) : 0;
      updateInterface(value);
      seekLatest();
    };

    const schedule = () => {
      if (!near || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };

    const size = () => {
      const rect = sticky.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      if (canvas.width === width && canvas.height === height) return;
      canvas.width = width;
      canvas.height = height;
      renderedFrame = -1;
      paint();
    };

    const loadSource = () => {
      if (sourceLoaded || !sourceReady) return;
      sourceLoaded = true;
      const compact = mobileMedia.matches || constrainedDevice();
      const source = compact ? (video.dataset.srcMobile || video.dataset.src) : video.dataset.src;
      if (!source) return;
      loadAbort?.abort();
      loadAbort = new AbortController();
      stage.dataset.mediaState = 'loading';
      fetch(source, { signal: loadAbort.signal, cache: 'force-cache' })
        .then((response) => {
          if (!response.ok) throw Error(`Canopy film failed: ${response.status}`);
          return response.blob();
        })
        .then((blob) => {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          objectUrl = URL.createObjectURL(blob);
          video.preload = 'auto';
          video.src = objectUrl;
          video.load();
        })
        .catch((error) => {
          if (error.name !== 'AbortError') stage.dataset.mediaState = 'fallback';
        });
    };

    const onMetadata = () => {
      duration = Number.isFinite(video.duration) ? video.duration : 0;
      update();
    };
    const onLoadedData = () => {
      paint();
      seekLatest();
    };
    const onSeeked = () => {
      paint();
      seekLatest();
    };
    const onError = () => {
      stage.dataset.mediaState = 'fallback';
    };
    const onViewportChange = () => {
      if (!sourceLoaded) return;
      sourceLoaded = false;
      duration = 0;
      renderedFrame = -1;
      loadSource();
    };

    const observer = new IntersectionObserver(([entry]) => {
      near = entry.isIntersecting;
      if (near) {
        loadSource();
        size();
        schedule();
      } else if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    }, { rootMargin: '175% 0px', threshold: 0 });
    const resize = new ResizeObserver(() => {
      size();
      schedule();
    });

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.addEventListener('loadedmetadata', onMetadata);
    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    mobileMedia.addEventListener('change', onViewportChange);
    addEventListener('scroll', schedule, { passive: true, signal: aborter.signal });
    observer.observe(stage);
    resize.observe(sticky);
    size();
    updateInterface(progress());

    clean.push(() => {
      destroyed = true;
      aborter.abort();
      observer.disconnect();
      resize.disconnect();
      cancelAnimationFrame(raf);
      loadAbort?.abort();
      clearTimeout(seekTimer);
      video.removeEventListener('loadedmetadata', onMetadata);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      mobileMedia.removeEventListener('change', onViewportChange);
      video.removeAttribute('src');
      video.load();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    });
  };

  const initWords = (stage, clean) => {
    const words = [...stage.querySelectorAll('[data-rvr3-word]')];
    if (reducedMedia.matches) {
      words.forEach((word) => word.classList.add('is-active'));
      return;
    }
    let visible = false;
    let raf = 0;
    const draw = () => {
      const rect = stage.getBoundingClientRect();
      const travel = Math.max(1, rect.height - innerHeight);
      const value = clamp(-rect.top / travel);
      const active = Math.ceil(value * words.length);
      words.forEach((word, index) => word.classList.toggle('is-active', index < active));
    };
    const schedule = () => {
      if (!visible || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        draw();
      });
    };
    const aborter = new AbortController();
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) schedule();
    }, { rootMargin: '100% 0px' });
    addEventListener('scroll', schedule, { passive: true, signal: aborter.signal });
    observer.observe(stage);
    draw();
    clean.push(() => {
      aborter.abort();
      observer.disconnect();
      cancelAnimationFrame(raf);
    });
  };

  const init = (root) => {
    if (roots.has(root)) return;
    const clean = [];
    root.dataset.rvr3Init = 'true';
    roots.set(root, () => {
      clean.splice(0).reverse().forEach((fn) => fn());
      delete root.dataset.rvr3Init;
      roots.delete(root);
    });
    root.querySelectorAll('[data-rvr3-canopy]').forEach((stage) => initCanopy(stage, clean));
    root.querySelectorAll('[data-rvr3-words]').forEach((stage) => initWords(stage, clean));
  };

  const find = (target) => (target.matches?.(ROOT_SELECTOR) ? target : target.querySelector?.(ROOT_SELECTOR));
  document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    const root = find(event.target);
    if (root) init(root);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const root = find(event.target);
    if (root) roots.get(root)?.();
  });
})();
